import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Params } from '@angular/router';
import { UtilsService } from '@core/services/utils.service';
import { DropdownPosition } from '@ng-select/ng-select';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { NgSelectLabelDirective } from '@shared/components/ng-select-typeahead/directives/ng-select-label-tmp.directive';
import { DropdownItem } from '@shared/models/common.model';
import { DropdownService } from '@shared/service/dropdown.service';
import * as _ from 'lodash';
import { map, Observable } from 'rxjs';
import { NgSelectMultiLabelDirective } from '../ng-select-typeahead/directives/ng-select-multi-label-tmp.directive';
import { NgSelectOptionDirective } from '../ng-select-typeahead/directives/ng-select-option-tmp.directive';
import { NgSelectTypeaheadComponent } from '../ng-select-typeahead/ng-select-typeahead.component';
import { ProfileDisplayComponent } from '../profile-display/profile-display.component';

@Component({
  selector: 'app-typeahead-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectTypeaheadComponent,
    NgSelectLabelDirective,
    NgSelectMultiLabelDirective,
    NgSelectOptionDirective,
    ProfileDisplayComponent,
    TranslateModule,
  ],
  templateUrl: './typeahead-dropdown.component.html',
  styleUrls: ['./typeahead-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TypeaheadDropdownComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: TypeaheadDropdownComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypeaheadDropdownComponent
  implements ControlValueAccessor, Validator, OnChanges, AfterViewInit
{
  @Input() addTag = false;
  @Input() addTagText!: string;
  @Input() bindLabel = 'label';
  @Input() bindSubLabel?: string;
  @Input() bindValue!: string;
  @Input() compareWith?: CompareWithFn;
  @Input() clearable = false;
  @Input() clearChoicesOnLessMinTerm = true;
  @Input() clearSearchOnAdd = false;
  @Input() closeOnSelect?: boolean;
  @Input() dataGeneratorConfig!: DataGeneratorConfig<any>;
  @Input() dropdownPosition?: DropdownPosition;
  @Input() hideSelected = false;
  @Input() imageConfig?: ImageConfig;
  @Input() multiple = false;
  @Input() ngSelectClass?: string;
  @Input() placeholder?: string;
  @Input() readonly = false;
  @Input() required: string | boolean = false;
  @Input() invalid = false;
  @Input() showImage = false;
  @Input() startWith?: Array<DropdownItem> | null;
  @Input() bindSubLabelInLabel: boolean;
  @Output() dropdownClose = new EventEmitter();
  @Output() selectedItemChange =
    new EventEmitter<DropdownItem | null>();
  @ViewChild(NgSelectTypeaheadComponent, { read: ElementRef })
  typeaheadComp?: ElementRef<HTMLElement>;

  _closeOnSelect = true;
  dataGenerator!: (search: string) => Observable<any[]>;
  selected?: DropdownItem<number>;

  get _compareWith(): CompareWithFn | undefined {
    if (this.compareWith) {
      return this.compareWith;
    }
    if (this.bindValue) {
      return undefined;
    }
    return compareDropdownItemByValue;
  }

  private _onTouch: any;
  private _onChange!: (val: any) => void;
  private translate = inject(TranslateService);
  private utils = inject(UtilsService);

  constructor(
    private cdRef: ChangeDetectorRef,
    private dropdownApi: DropdownService,
  ) {
    this.subscribeTranslation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataGeneratorConfig']?.currentValue) {
      const config = changes['dataGeneratorConfig']
        .currentValue as DataGeneratorConfig<any>;
      this.dataGenerator = this.parseDataGenerator(config);
    } else if (!this.dataGeneratorConfig) {
      throw new Error(
        'The dataGeneratorConfig is required! for querying dropdown api',
      );
    }
    if (changes['multiple'] || changes['closeOnSelect']) {
      if (this.closeOnSelect == null) {
        this._closeOnSelect = !this.multiple;
      }
    }
  }

  ngAfterViewInit(): void {
    this.setPlaceholder();
  }

  getImageSrc(item: { [k: string]: any }): string | undefined {
    if (!this.imageConfig?.bindSrc) {
      return;
    }
    return _.get(item, this.imageConfig.bindSrc);
  }

  getImageType(item: { [k: string]: any }): string | undefined {
    if (!this.imageConfig?.bindType) {
      return 'person';
    }
    return _.get(item, this.imageConfig.bindType);
  }

  getImageStyleClass(item: { [k: string]: any }): string | undefined {
    return (
      this.imageConfig?.bindImageStyleClass &&
      _.get(item, this.imageConfig.bindImageStyleClass)
    );
  }

  getLabel(item: any): string | undefined {
    return _.get(item, this.bindLabel);
  }

  // *IMPORTANT* This function is used inside the content view where
  // * the change detection strategy is not OnPush. Therefore, it uses more performance.
  // TODO: Improve the app-ng-select-typeahead component to use the OnPush change detection strategy.
  getSubLabel(item: any): string | undefined {
    if (!this.bindSubLabel) {
      return;
    }
    const subLabel = _.get(item, this.bindSubLabel);
    if (!subLabel) {
      return;
    }
    if (typeof subLabel === 'string') {
      return this.translate.instant(subLabel);
    } else if (typeof subLabel === 'object') {
      return this.translate.instant(subLabel.text, subLabel.params);
    }
    return;
  }

  onDropdownClose(): void {
    this.dropdownClose.emit();
  }

  onValueChange(val: DropdownItem<number>) {
    if (this._onChange) {
      this._onChange(val);
    }
  }

  onSelectedItem(item: DropdownItem | null) {
    this.selectedItemChange.emit(item);
  }

  parseDataGenerator(
    config: DataGeneratorConfig<any>,
  ): (search: string) => Observable<any[]> {
    if (typeof config === 'function') {
      return config;
    } else if (typeof config === 'object') {
      return (search: string) => {
        const type = config.type;
        if (type == null) {
          throw new Error(
            'The type in dataGeneratorConfig is required',
          );
        }
        this.cdRef.markForCheck();
        return this.dropdownApi
          .getDropdown({
            type,
            ...this.utils.getValidQueryParams(config.params || {}, {
              nullable: true,
            }),
            [config.typingParamName || 'query']: search,
          })
          .pipe(
            map((res) => res[type]),
            map((res) =>
              config.transformer ? res.map(config.transformer) : res,
            ),
          );
      };
    } else {
      throw new Error('The type of dataGeneratorConfig is invalid');
    }
  }

  registerOnTouched(fn: any) {
    this._onTouch = fn;
  }

  registerOnChange(fn: (val: any) => void) {
    this._onChange = fn;
  }

  setPlaceholder(): void {
    const typeaheadInput =
      this.typeaheadComp?.nativeElement.querySelector('.ng-input')
        ?.children[0] as HTMLInputElement | undefined;
    if (typeaheadInput) {
      typeaheadInput.placeholder =
        this.translate.instant('COMMON.Search') + '...';
    }
  }

  subscribeTranslation(): void {
    this.translate.onLangChange.subscribe({
      next: () => {
        this.setPlaceholder();
      },
    });
  }

  validate(): ValidationErrors | null {
    if (!this.required) {
      return null;
    }
    return this.selected
      ? null
      : {
          selection: {
            required: true,
          },
        };
  }

  writeValue(val: DropdownItem<number>): void {
    this.selected = val;
  }
}

export type CompareWithFn = (a: any, b: any) => boolean;

export const compareDropdownItemByValue: CompareWithFn = (a, b) =>
  a === b || (a?.value != null && a.value === b?.value);

export interface DropdownGeneratorConfig<T = any> {
  params?: Params;
  transformer?: (items: DropdownItem<any>) => T;
  type: string;
  typingParamName?: string;
}

export type DataGeneratorConfig<T> =
  | DropdownGeneratorConfig<T>
  | ((search: string) => Observable<T[]>);

export interface ImageConfig {
  bindImageStyleClass?: string;
  bindType?: string;
  bindSrc?: string;
}
